import { useState, useId } from 'react';
import { getApiUrl } from '../lib/api';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$|^[a-z0-9]{3,40}$/;

function validateSlug(v: string): string | null {
  if (!v) return 'Workspace URL is required';
  if (v.length < 3) return 'Must be at least 3 characters';
  if (v.length > 40) return 'Must be 40 characters or fewer';
  if (!SLUG_RE.test(v)) return 'Lowercase letters, numbers, and hyphens only — no leading/trailing hyphen';
  return null;
}

function validateEmail(v: string): string | null {
  if (!v) return 'Email is required';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return 'Enter a valid email address';
  return null;
}

function validateName(v: string): string | null {
  if (!v.trim()) return 'Workspace name is required';
  if (v.trim().length < 2) return 'Must be at least 2 characters';
  if (v.trim().length > 80) return 'Must be 80 characters or fewer';
  return null;
}

export type SignupFormProps = {
  /** Override the API call for testing */
  onSubmit?: (data: { email: string; tenantName: string; tenantSlug: string }) => Promise<{ url: string }>;
};

export function SignupForm({ onSubmit }: SignupFormProps) {
  const emailId = useId();
  const nameId = useId();
  const slugId = useId();

  const [email, setEmail] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const slugError = slugTouched ? validateSlug(tenantSlug) : null;
  const slugValid = !validateSlug(tenantSlug);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSlugTouched(true);

    const emailErr = validateEmail(email);
    const nameErr = validateName(tenantName);
    const slugErr = validateSlug(tenantSlug);
    if (emailErr || nameErr || slugErr) return;

    setSubmitting(true);
    setApiError(null);

    try {
      let result: { url: string };

      if (onSubmit) {
        result = await onSubmit({ email, tenantName, tenantSlug });
      } else {
        const res = await fetch(`${getApiUrl()}/billing/subscribe/pilot`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, tenantName, tenantSlug }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => 'unknown error');
          setApiError(text || 'Something went wrong. Please try again.');
          setSubmitting(false);
          return;
        }
        result = await res.json();
      }

      window.location.assign(result.url);
    } catch {
      setApiError('Network error — please check your connection and try again.');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {/* Email */}
      <div>
        <label htmlFor={emailId} className="block text-sm font-medium text-ink-mute mb-1.5">
          Work email
        </label>
        <input
          id={emailId}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
          className="w-full rounded-lg border border-hairline bg-surface px-4 py-2.5 text-ink placeholder-ink-mute focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
        />
      </div>

      {/* Tenant name */}
      <div>
        <label htmlFor={nameId} className="block text-sm font-medium text-ink-mute mb-1.5">
          Workspace name
        </label>
        <input
          id={nameId}
          type="text"
          autoComplete="organization"
          value={tenantName}
          onChange={(e) => setTenantName(e.target.value)}
          placeholder="Acme Co"
          required
          className="w-full rounded-lg border border-hairline bg-surface px-4 py-2.5 text-ink placeholder-ink-mute focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
        />
      </div>

      {/* Tenant slug */}
      <div>
        <label htmlFor={slugId} className="block text-sm font-medium text-ink-mute mb-1.5">
          Workspace URL
        </label>
        <div className="relative flex items-center rounded-lg border border-hairline bg-surface focus-within:border-accent focus-within:ring-1 focus-within:ring-accent transition-colors overflow-hidden">
          <span className="flex-shrink-0 pl-4 pr-1 text-mute text-sm select-none pointer-events-none">
            clarity.app/
          </span>
          <input
            id={slugId}
            type="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            value={tenantSlug}
            onChange={(e) => {
              setTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              setSlugTouched(true);
            }}
            onBlur={() => setSlugTouched(true)}
            placeholder="acme-co"
            required
            className="flex-1 bg-transparent py-2.5 pr-4 text-ink placeholder-ink-mute focus:outline-none"
          />
          {tenantSlug.length > 0 && (
            <span
              aria-hidden
              className={`flex-shrink-0 mr-3 text-xs font-semibold px-1.5 py-0.5 rounded ${
                slugValid
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-red-500/15 text-red-400'
              }`}
            >
              {slugValid ? '✓' : '✗'}
            </span>
          )}
        </div>
        {slugError && (
          <p role="alert" className="mt-1.5 text-xs text-red-400">
            {slugError}
          </p>
        )}
      </div>

      {/* API error */}
      {apiError && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {apiError}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full mt-2 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? 'Redirecting to payment…' : 'Continue to payment →'}
      </button>

      {/* Login link */}
      {/* TODO: wire /login route in Task 1.19 */}
      <p className="text-center text-sm text-mute">
        Already have a workspace?{' '}
        <a href="/login" className="transition-colors" style={{ color: 'var(--color-accent)' }}>
          Log in &rarr;
        </a>
      </p>
    </form>
  );
}
